import { FileText } from 'lucide-react';
import { SectionMessage } from '@/components/shared/SectionMessage';
import { sectionFailure } from '@/components/shared/section-messages';
import { useActiveSearch } from '@/lib/active-search';
import { formatLongDate, formatShortDate, formatText, safeHttpUrl } from '@/utils/format';
import { usePolicySearch } from '../hooks/use-policy-search';
import { PolicyholderSkeleton } from './PolicyholderSkeleton';

const UNAVAILABLE_MESSAGE =
  'Policy information is temporarily unavailable. Please try again in a few minutes.';
const NO_PLAN_DOCUMENT_MESSAGE = 'The plan document is not available for this policy.';

/**
 * Policyholder Information (US-008, US-009 AC-001). Renders at the top of the results page, above the
 * claims grid, with no tab navigation between the two.
 */
export function PolicyholderSection(): React.JSX.Element | null {
  const { data: criteria } = useActiveSearch();
  const { data: policy, isPending, isError, error } = usePolicySearch();

  // Nothing has been searched yet. `fetchStatus` cannot answer this: it returns to 'idle' after a
  // failed request too, which would silently swallow the error state.
  if (criteria === null || criteria === undefined) {
    return null;
  }

  if (isPending) {
    return <PolicyholderSkeleton />;
  }

  // A failed refetch does not throw away what was already retrieved: PP-028 AC-003 asks for the most
  // recent data to stay on screen while the backend is unreachable, and `policy` is only set when a
  // call succeeded. The banner above the page says the figures may be out of date.
  if (isError && policy === undefined) {
    return <LookupFailed error={error} status={error.status} />;
  }

  return (
    <section
      aria-labelledby="policyholder-heading"
      className="border-line rounded-xl border bg-surface p-6 shadow-sm"
    >
      <h2
        id="policyholder-heading"
        className="text-brand-600 border-line border-b pb-3 text-lg font-bold"
      >
        Policyholder Information
      </h2>

      <dl className="mt-4 grid gap-x-10 gap-y-3 sm:grid-cols-2">
        <Field label="Policy Number" value={formatText(policy.policyNumber)} />
        <Field label="Coverage" value={<CoverageLink policy={policy} />} />
        <Field label="First Name" value={formatText(policy.firstName)} />
        <Field label="Issue Date" value={formatLongDate(policy.issueDate)} />
        <Field label="Last Name" value={formatText(policy.lastName)} />
        <Field label="Date of Birth" value={formatShortDate(policy.dateOfBirth)} />
        <Field label="Termed Date" value={formatLongDate(policy.terminationDate)} />
        <Field label="Age" value={formatText(policy.age)} />
        <Field label="Policy Status" value={formatText(policy.policyStatus)} />
      </dl>
    </section>
  );
}

/**
 * Which failure this was is decided in `sectionFailure`, so Claims below says the same thing — and
 * null means the portal could not be reached at all, which PP-028 answers once for the whole page
 * rather than here.
 */
function LookupFailed({
  error,
  status,
}: {
  error: unknown;
  status: number | undefined;
}): React.JSX.Element | null {
  const failure = sectionFailure(error, status, UNAVAILABLE_MESSAGE);
  if (failure === null) {
    return null;
  }

  return (
    <SectionMessage title="Policyholder Information" tone={failure.tone} message={failure.message} />
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex gap-4 text-sm">
      <dt className="text-muted w-36 flex-none font-semibold">{label}</dt>
      <dd className="text-ink min-w-0 break-words">{value}</dd>
    </div>
  );
}

function CoverageLink({
  policy,
}: {
  policy: { coverageDescription: string | null; coverageDocumentUrl: string | null };
}): React.JSX.Element {
  const label = formatText(policy.coverageDescription);
  const href = safeHttpUrl(policy.coverageDocumentUrl);

  // US-016 AC-003/AC-004: a missing or unusable document URL says so in plain words rather than
  // leaving a bare description where a link belongs. It never says why — a retrieval failure and an
  // absent URL read the same, and no API detail reaches the screen.
  if (href === null) {
    return (
      <>
        {label}
        <span className="text-subtle mt-0.5 block text-xs">{NO_PLAN_DOCUMENT_MESSAGE}</span>
      </>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-brand-500 inline-flex items-center gap-1.5 underline"
    >
      <FileText aria-hidden="true" className="h-4 w-4" />
      {label} [PDF]
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}
